import { CategoryService } from './../items/services/categories/category.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { supplier } from './models/supplier';
import { BreadcrumbsComponent } from '../shared/components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbsOptions } from '../shared/models/breadcrumbs-model';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { AlertComponent } from '../shared/components/dialogs/alert/alert.component';
import { ConfirmDialogComponent } from '../shared/components/dialogs/confirm/confirm.component';
import { SuppliersService } from './services/suppliers.service';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { category } from '../items/models/category';
import { Page } from '../shared/models/page.model';
import {MatTooltip} from '@angular/material/tooltip';

@Component({
  selector: 'app-suppliers',
  templateUrl: './suppliers.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatTableModule,
    MatSelectModule,
    MatOptionModule,
    MatPaginator,
    MatPaginatorModule,
    TranslateModule,
    FlexLayoutModule,
    BreadcrumbsComponent,
    NgxMaskDirective,
    MatTooltip
  ],
  providers: [
    provideNgxMask({dropSpecialCharacters: false}),
  ],
  styleUrl: './suppliers.component.scss'
})
export class SuppliersComponent {
  breadcrumbsData: BreadcrumbsOptions = {
    title: 'supplier.suppliers',
    buttons: [
      {
        label: 'button.back',
        style: 'blue',
        router: '/orders-dashboard'
      }
    ]
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('form') supplierFormViewChild: any;

  suppliers: supplier[] = [];
  categories: category[] = [];

  supplierForm: FormGroup;
  dataSource = new MatTableDataSource<supplier>();
  filter: FormControl = new FormControl('');
  pageable: PageEvent = {
    pageIndex: 0,
    pageSize: 5,
    length: 0
  }

  isEdit: boolean = false;
  editingSupplierId: number | null = null;

  displayedColumns: string[] = ['cnpj', 'corporateName', 'contactName', 'contactEmail', 'category', 'actions'];
  readonly dialog = inject(MatDialog);

  constructor(private formBuilder: FormBuilder, private suppliersService: SuppliersService, private categoryService: CategoryService) {
    this.supplierForm = this.formBuilder.group({
      cnpj: ['', [Validators.required, Validators.pattern(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})-(\d{2})$/)]],
      corporateName: ['', Validators.required],
      category: ['', Validators.required],
      contactName: ['', Validators.required],
      contactEmail: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit() {
    this.loadSuppliers();
    this.loadCategories();
    this.filter.valueChanges.subscribe((filterValue) => {
      this.pageable.pageIndex = 0;
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.loadSuppliers((filterValue ?? '').trim().toLowerCase());
    });
}

  loadSuppliers(filter: string = '') {
    this.suppliersService.getSuppliers(this.pageable.pageIndex, this.pageable.pageSize, filter).subscribe(
      (page: Page<supplier>) => {
        this.dataSource.data = page.content;
        this.pageable.length = page.totalElements;
      },
    )
  }

  loadCategories() {
    this.categoryService.listCategories().subscribe({
      next: (categories) => {
        this.categories = categories;
      }
    })
  }

  onPageChange(event: PageEvent) {
    this.pageable.pageIndex = event.pageIndex;
    this.pageable.pageSize = event.pageSize;
    const filterValue = this.filter.value?.trim().toLowerCase() || '';
    this.loadSuppliers(filterValue);
  }

  reset() {
    this.supplierFormViewChild.resetForm();
  }

  createSupplier() {
    const categoryName = this.supplierForm.value.category;
    const category = this.categories.find(cat => cat.category === categoryName);

    const newSupplier: supplier = {
      ...this.supplierForm.value,
      category: {
        id: category?.id,
        category: category?.category
      },
      active: true
    };

    this.suppliersService.createSupplier(newSupplier).subscribe(
      () => {
        this.loadSuppliers();
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'supplier.createdSuccess',
            param: { supplierName: newSupplier.corporateName },
            title: 'alert.information'
          }
        });
        this.reset();
      },
      (error) => {
        if (error.status === 409) {
          const dialogRef = 'supplier.alreadyExists';
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: dialogRef,
              param: null,
              title: 'alert.information',
            },
          });
        }
      },
    );
  }


  startUpdate(supplier: any) {
    this.isEdit = true;
    this.editingSupplierId = supplier.id;
    this.supplierForm.patchValue({
      cnpj: supplier.cnpj,
      corporateName: supplier.corporateName,
      category: supplier.category?.category,
      contactName: supplier.contactName,
      contactEmail: supplier.contactEmail
    });
  }

  updateSupplier() {
    if (this.supplierForm.valid && this.editingSupplierId !== null) {
      const categoryName = this.supplierForm.value.category;
      const category = this.categories.find(cat => cat.category === categoryName);
      const updatedSupplier: supplier = {
        id: this.editingSupplierId,
        ...this.supplierForm.value,
        category: {
          id: category?.id,
          category: category?.category
        }
      };
      this.suppliersService.updateSupplier(this.editingSupplierId, updatedSupplier).subscribe({
        next: () => {
          this.loadSuppliers();
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: 'supplier.updatedSuccess',
              param: { supplierName: updatedSupplier.corporateName },
              title: 'alert.information'
            }
          });
          this.reset();
          this.isEdit = false;
          this.editingSupplierId = null;
        },
        error: (err) => {
          if (err.status === 409) {
            const dialogRef = 'supplier.alreadyExists';
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                message: dialogRef,
                param: null,
                title: 'alert.information',
              },
            });
          }
        }
      });
    }
  }

  deleteSupplier(supplier: supplier) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: "supplier.deleteSupplierConfirmation",
        param: { supplierName: supplier.corporateName }
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirm) {
        this.suppliersService.deleteSupplier(supplier, supplier.id).subscribe({
          next: () => {
            this.loadSuppliers();
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                message: 'supplier.deletedSuccess',
                param: { supplierName: supplier.corporateName },
                title: 'alert.information'
              }
            });
          },
          error: (error) => {
            if (error.status === 409 && error.error.message === 'Supplier associated to at least a budget') {
              this.dialog.open(AlertComponent, {
                disableClose: true,
                data: {
                  message: 'alert.cannotDeleteSupplier',
                  title: 'alert.information',
                },
              });
            }
          }
        });
        this.reset();
      }
    });
  }
}
